"use client";

/* eslint-disable react/only-export-components -- co-located helper. */

import * as React from "react";
import { Code } from "lucide-react";
import { useFilePreview } from "../../../hooks/use-file-preview";
import { PreviewLoading } from "./preview-loading";

export interface HtmlPreviewProps {
  fileUrl?: string | undefined;
  filename: string;
  citedText: string;
}

const HTML_PREVIEW_HEIGHT = 230;

/**
 * Inject a highlight script + style into raw HTML content.
 * Uses TreeWalker to find text nodes matching `citedText`,
 * wraps them with <mark>, and scrolls the first match into view.
 *
 * Safe to use inside `sandbox="allow-scripts"` (no allow-same-origin).
 */
export function injectCitedTextHighlight(html: string, citedText: string): string {
  if (!citedText.trim()) return html;

  const escaped = citedText
    .replace(/\\/g, "\\\\")
    .replace(/<\//g, "<\\/")
    .replace(/'/g, "\\'")
    .replace(/\n/g, "\\n")
    .replace(/\r/g, "\\r");

  const highlightStyle = `<style data-highlight>
mark[data-cite-hl]{background:rgba(59,130,246,0.25);outline:2px solid rgba(59,130,246,0.45);border-radius:2px;padding:0 1px}
</style>`;

  const highlightScript = `<script data-highlight>
(function(){
  var q='${escaped}'.toLowerCase();
  if(!q)return;
  var walker=document.createTreeWalker(document.body,NodeFilter.SHOW_TEXT,null);
  var nodes=[];
  while(walker.nextNode())nodes.push(walker.currentNode);
  var first=null;
  for(var i=0;i<nodes.length;i++){
    var node=nodes[i];
    var idx=node.textContent.toLowerCase().indexOf(q);
    if(idx===-1)continue;
    var before=node.textContent.slice(0,idx);
    var match=node.textContent.slice(idx,idx+q.length);
    var after=node.textContent.slice(idx+q.length);
    var mark=document.createElement('mark');
    mark.setAttribute('data-cite-hl','');
    mark.textContent=match;
    var parent=node.parentNode;
    if(before)parent.insertBefore(document.createTextNode(before),node);
    parent.insertBefore(mark,node);
    if(after)parent.insertBefore(document.createTextNode(after),node);
    parent.removeChild(node);
    if(!first)first=mark;
  }
  if(first)first.scrollIntoView({block:'center',behavior:'smooth'});
})();
${"<"}/script>`;

  if (html.includes("</body>")) {
    return html.replace("</body>", highlightStyle + highlightScript + "</body>");
  }
  return html + highlightStyle + highlightScript;
}

export function HtmlPreview({ fileUrl, filename, citedText }: HtmlPreviewProps) {
  const { content, isLoading } = useFilePreview(fileUrl);

  const srcDoc = React.useMemo(
    () => (content ? injectCitedTextHighlight(content, citedText) : ""),
    [content, citedText],
  );

  if (!fileUrl || (!isLoading && !content)) {
    return (
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-2 text-xs text-orange-600 dark:text-orange-400 font-medium">
          <Code className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="truncate">{filename}</span>
        </div>
        {/* quovy-lint-ignore prefer-notice -- quoted-citation excerpt box, not a status message */}
        <div className="bg-orange-50 dark:bg-orange-950/30 rounded-md p-2 border border-orange-100 dark:border-orange-900">
          <p className="text-sm text-stone-600 dark:text-stone-300 leading-relaxed line-clamp-4">
            {citedText}
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) return <PreviewLoading />;

  return (
    <div className="relative bg-white flex flex-col" style={{ height: HTML_PREVIEW_HEIGHT }}>
      <div className="flex items-center gap-2 px-3 py-1.5 border-b bg-muted/80 text-xs text-muted-foreground">
        <Code className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="truncate">{filename}</span>
      </div>
      <div className="flex-1 min-h-0">
        <iframe
          srcDoc={srcDoc}
          title={`HTML preview: ${filename}`}
          className="h-full w-full border-0 bg-white"
          sandbox="allow-scripts"
        />
      </div>
    </div>
  );
}
